import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'aldoo-ra/Modal';
import Button from 'aldoo-ra/Button';
import Typography from 'aldoo-ra/Typography';
import Input from 'aldoo-ra/Input';
import { PlayIcon, PlusIcon, MinusIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import { Editor } from '@monaco-editor/react';
import { showNotification } from 'aldoo-ra/Notification';

/**
 * Run Script Dialog - handles script selection and execution
 */
export default function RunScriptDialog({ isOpen, onClose, onRunSuccess, controller }) {
    const [availableScripts, setAvailableScripts] = useState([]);
    const [selectedScript, setSelectedScript] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [envVars, setEnvVars] = useState([{ key: '', value: '' }]);
    const [showEnvVars, setShowEnvVars] = useState(false);
    const [showSourceDialog, setShowSourceDialog] = useState(false);
    const [scriptSource, setScriptSource] = useState('');
    const [scriptLanguage, setScriptLanguage] = useState('lua');

    useEffect(() => {
        if (isOpen) {
            loadAvailableScripts();
        }
    }, [isOpen]);

    const loadAvailableScripts = async () => {
        try {
            const scripts = await controller.getScripts();
            setAvailableScripts(scripts);
        } catch (error) {
            console.error('Failed to load scripts:', error);
            showNotification({
                message: 'Failed to load available scripts',
                type: 'error'
            });
        }
    };

    const handleSelectScript = (script) => {
        setSelectedScript(script);

        // Populate environment variables from script.env
        if (script.env && Object.keys(script.env).length > 0) {
            const envVarsArray = Object.entries(script.env).map(([key, value]) => ({
                key,
                value: String(value)
            }));
            setEnvVars(envVarsArray);
            setShowEnvVars(true);
        } else {
            setEnvVars([{ key: '', value: '' }]);
            setShowEnvVars(false);
        }
    };

    const handleRunScript = async () => {
        if (!selectedScript) return;

        try {
            // Build env object from envVars array
            const env = {};
            envVars.forEach(({ key, value }) => {
                if (key && value) {
                    env[key] = value;
                }
            });

            const response = await controller.runScript(
                selectedScript.name,
                Object.keys(env).length > 0 ? env : undefined
            );

            // Handle wrapped response format
            const data = response.data || response;

            if (response?.success) {
                showNotification({
                    message: `Task "${data.taskName || selectedScript.name}" started successfully`,
                    type: 'success'
                });
                setSelectedScript(null);
                setSearchQuery('');
                setEnvVars([{ key: '', value: '' }]);
                setShowEnvVars(false);
                onRunSuccess();
            } else {
                showNotification({
                    message: data.error || 'Failed to run script',
                    type: 'error'
                });
            }
        } catch (error) {
            showNotification({
                message: error.message || 'Failed to run script',
                type: 'error'
            });
        }
    };

    const handleViewSource = async () => {
        if (!selectedScript) return;

        try {
            console.log('[handleViewSource] Fetching source for:', selectedScript.name);
            const response = await controller.getScriptSource(selectedScript.name);
            console.log('[handleViewSource] Response:', response);

            // Handle wrapped response format
            const data = response.data || response;

            if (data.success && data.source) {
                setScriptSource(data.source);
                setScriptLanguage(data.language || 'lua');
                setShowSourceDialog(true);
            } else {
                showNotification({
                    message: data.error || 'Failed to load script source',
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('[handleViewSource] Error:', error);
            showNotification({
                message: `Failed to load script source: ${error.message}`,
                type: 'error'
            });
        }
    };

    const handleAddEnvVar = () => {
        setEnvVars([...envVars, { key: '', value: '' }]);
    };

    const handleRemoveEnvVar = (index) => {
        if (envVars.length === 1) {
            setEnvVars([{ key: '', value: '' }]);
        } else {
            setEnvVars(envVars.filter((_, i) => i !== index));
        }
    };

    const handleEnvVarChange = (index, field, value) => {
        const newEnvVars = [...envVars];
        newEnvVars[index][field] = field === 'key' ? value.trim() : value;
        setEnvVars(newEnvVars);
    };

    const filteredScripts = useMemo(() => {
        if (!searchQuery) return availableScripts;
        const query = searchQuery.toLowerCase();
        return availableScripts.filter(script =>
            script.name.toLowerCase().includes(query) ||
            script.description.toLowerCase().includes(query)
        );
    }, [availableScripts, searchQuery]);

    const handleClose = () => {
        setSelectedScript(null);
        setSearchQuery('');
        setEnvVars([{ key: '', value: '' }]);
        setShowEnvVars(false);
        onClose();
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title="Run Task"
                variant="admin_centered"
                styleOverrides={{
                    container: "w-full max-w-5xl max-h-full md:max-h-[calc(100vh-4rem)] bg-admin_paper overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper md:rounded-lg border border-admin_bordered shadow-xl mx-4"
                }}
            >
                <div className="flex h-[600px]">
                    {/* Left Column - Script List */}
                    <div className="w-1/3 border-r border-admin_bordered flex flex-col">
                        <div className="p-4 border-b border-admin_bordered">
                            <Input
                                variant="admin"
                                placeholder="Search scripts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                showClearButton={true}
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas">
                            {filteredScripts.length > 0 ? (
                                filteredScripts.map((script) => (
                                    <div
                                        key={script.name}
                                        onClick={() => handleSelectScript(script)}
                                        className={`p-3 cursor-pointer border-b border-admin_bordered transition-colors ${
                                            selectedScript?.name === script.name
                                                ? 'bg-admin_info/10 border-l-4 border-l-admin_info'
                                                : 'hover:bg-admin_neutral_1'
                                        }`}
                                    >
                                        <Typography variant="admin_txt2" className="font-semibold text-admin_text">
                                            {script.name}
                                        </Typography>
                                    </div>
                                ))
                            ) : (
                                <div className="p-8 text-center">
                                    <Typography variant="admin_body" className="text-admin_text_secondary">
                                        {searchQuery ? 'No scripts found matching your search' : 'No scripts available'}
                                    </Typography>
                                </div>
                            )}
                        </div>
                        <div className="px-3 border-t border-admin_bordered bg-admin_neutral_1">
                            <Typography variant="admin_body" className="text-xs text-admin_text_secondary">
                                {filteredScripts.length} {filteredScripts.length === 1 ? 'script' : 'scripts'}
                                {searchQuery && filteredScripts.length !== availableScripts.length &&
                                    ` (${availableScripts.length} total)`
                                }
                            </Typography>
                        </div>
                    </div>

                    {/* Right Column - Script Details */}
                    <div className="w-2/3 flex flex-col">
                        {selectedScript ? (
                            <>
                                <div className="p-6 border-b border-admin_bordered">
                                    <Typography variant="admin_h3" className="font-semibold mb-2">
                                        {selectedScript.name}
                                    </Typography>
                                    <Typography variant="admin_body" className="text-admin_text_secondary">
                                        {selectedScript.description}
                                    </Typography>
                                </div>

                                <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas p-6">
                                    <div className="space-y-4">
                                        <button
                                            onClick={() => setShowEnvVars(!showEnvVars)}
                                            className="flex items-center gap-2 text-admin_text hover:text-admin_info transition-colors"
                                        >
                                            <PlusIcon className="w-4 h-4" />
                                            <Typography variant="admin_txt2">Environment Variables</Typography>
                                        </button>

                                        {showEnvVars && (
                                            <div className="space-y-2">
                                                <div className="border border-admin_bordered rounded-md overflow-hidden">
                                                    <table className="w-full">
                                                        <thead className="bg-admin_neutral_1">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-admin_text_secondary uppercase">Key</th>
                                                                <th className="px-3 py-2 text-left text-xs font-medium text-admin_text_secondary uppercase">Value</th>
                                                                <th className="px-3 py-2 w-12"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-admin_bordered">
                                                            {envVars.map((envVar, index) => (
                                                                <tr key={index} className="bg-admin_canvas">
                                                                    <td className="px-3 py-2">
                                                                        <Input
                                                                            variant="admin"
                                                                            value={envVar.key}
                                                                            onChange={(e) => handleEnvVarChange(index, 'key', e.target.value)}
                                                                            placeholder="KEY_NAME"
                                                                            className="text-sm"
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <Input
                                                                            variant="admin"
                                                                            value={envVar.value}
                                                                            onChange={(e) => handleEnvVarChange(index, 'value', e.target.value)}
                                                                            placeholder="value"
                                                                            className="text-sm"
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center">
                                                                        <button
                                                                            onClick={() => handleRemoveEnvVar(index)}
                                                                            className="p-1 hover:bg-admin_error/10 rounded transition-colors"
                                                                            title="Remove variable"
                                                                        >
                                                                            <MinusIcon className="w-4 h-4 text-admin_error" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <button
                                                    onClick={handleAddEnvVar}
                                                    className="flex items-center gap-1 text-sm text-admin_info hover:text-admin_info/80 transition-colors"
                                                >
                                                    <PlusIcon className="w-4 h-4" />
                                                    Add Variable
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="p-6 border-t border-admin_bordered flex justify-end gap-2">
                                    <Button
                                        variant="admin_secondary"
                                        onClick={handleViewSource}
                                        disabled={!selectedScript}
                                        className="flex items-center gap-2"
                                    >
                                        <CodeBracketIcon className="w-4 h-4" />
                                        Source
                                    </Button>
                                    <Button
                                        variant="admin_primary"
                                        onClick={handleRunScript}
                                        className="flex items-center gap-2"
                                    >
                                        <PlayIcon className="w-4 h-4" />
                                        Run
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center p-6">
                                <Typography variant="admin_body" className="text-admin_text_secondary">
                                    Select a script to view details
                                </Typography>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Source Viewer Modal */}
            {showSourceDialog && (
                <Modal
                    isOpen={showSourceDialog}
                    onClose={() => setShowSourceDialog(false)}
                    title={selectedScript?.name || 'Script Source'}
                    variant="admin"
                    closeOnEscape={true}
                    styleOverrides={{
                        container: "w-full max-w-4xl max-h-[80vh] bg-admin_paper md:rounded-lg border border-admin_bordered shadow-xl"
                    }}
                >
                    <div className="overflow-hidden" style={{ height: '500px' }}>
                        <Editor
                            width="100%"
                            height="100%"
                            language={scriptLanguage}
                            value={scriptSource}
                            theme="vs-dark"
                            options={{
                                readOnly: true,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                lineNumbers: "on",
                                wordWrap: "on",
                                fontSize: 14,
                                automaticLayout: true
                            }}
                        />
                    </div>
                </Modal>
            )}
        </>
    );
}
